import React, { useEffect } from 'react';

import { topNavActionPlacement } from '../shared/topNavActionPlacement';
import type { MobileBottomAction, MobileNavItemConfig } from '../types';
import MobileAccordionItem from './MobileAccordionItem';

interface MobileNavOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  navItems: MobileNavItemConfig[];
  bottomActions: MobileBottomAction[];
  logoUrl: string;
}

export default function MobileNavOverlay({
  isOpen,
  onClose,
  navItems,
  bottomActions,
  logoUrl,
}: MobileNavOverlayProps): JSX.Element | null {
  // Lock body scroll and handle Escape key
  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = 'hidden';

    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);

    return (): void => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white">
      {/* Header — Logo + Close */}
      <div className="flex flex-shrink-0 items-center justify-between p-4">
        <a href="/" className="flex-shrink-0 no-underline">
          <img src={logoUrl} alt="Storyblocks" width={151} height={32} />
        </a>
        <button type="button" onClick={onClose} aria-label="Close menu">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="#1F2830"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* Nav items — scrollable */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex flex-col gap-6">
          {navItems.map((item) => (
            <MobileAccordionItem key={item.id} config={item} />
          ))}
        </div>
      </div>

      {/* Bottom actions */}
      {bottomActions.length > 0 && (
        <div className="flex flex-shrink-0 flex-col gap-2 p-4">
          {bottomActions.map((action) => (
            <a
              key={action.href}
              href={action.href}
              data-cta={topNavActionPlacement(action.href)}
              className={`flex h-14 items-center justify-center rounded-lg text-sm font-semibold no-underline ${
                action.variant === 'primary'
                  ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-500'
                  : 'border-2 border-gray-800 text-gray-800 hover:bg-gray-50'
              }`}
            >
              {action.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
